// Environment-agnostic analytics client.
//
// Phase 1 behavior:
//   - no real PostHog transport (posthog-js / posthog-node are [PHASE-2]);
//   - when `debug` is true, every call is echoed through console.warn;
//   - every call is buffered on the instance so tests and Phase 2 flushers can
//     inspect the tape.
//
// Invariants:
//   - `consent === 'denied'` → all emissions become no-ops (still buffered? NO,
//     we drop them entirely — this is the whole point of consent).
//   - Identical `(event, properties)` within 500ms is deduped.
//   - Identical `page(url)` within 1000ms is debounced.
//   - `anonymizeIp: true` (default) forces `$ip: null` into every payload.

import type {
  AnalyticsClient,
  AnalyticsConfig,
  Consent,
  EventMap,
  EventName,
  GroupTraits,
  IdentifyTraits,
  Scope,
} from './types.js';
import { hashWorkspaceId } from './privacy.js';

const DEDUPE_WINDOW_MS = 500;
const PAGE_DEBOUNCE_MS = 1000;

function readProcessEnv(name: string): string | undefined {
  const proc =
    typeof globalThis !== 'undefined'
      ? (globalThis as { process?: { env?: Record<string, string | undefined> } }).process
      : undefined;
  return proc?.env?.[name];
}

export interface BufferedEvent {
  readonly kind: 'track' | 'identify' | 'page' | 'group';
  readonly event?: EventName;
  readonly properties?: unknown;
  readonly scope?: Partial<Scope> | undefined;
  readonly at: number;
}

export interface ClientBaseOptions extends AnalyticsConfig {
  /** Overrides the default `console.warn` sink (used by tests). */
  sink?: (message: string, payload: unknown) => void;
}

/**
 * Shared implementation. Browser and Node entrypoints wrap this with any
 * environment-specific side effects (e.g. auto page-view on route change).
 *
 * TODO [PHASE-2]: plug real PostHog transports here (posthog-js on the client,
 * posthog-node on the server). Until then we keep everything in-memory.
 */
export class ClientBase implements AnalyticsClient {
  readonly #opts: Required<
    Pick<AnalyticsConfig, 'posthogHost' | 'debug' | 'anonymizeIp' | 'salt'>
  > &
    AnalyticsConfig;
  readonly #sink: (message: string, payload: unknown) => void;
  readonly #events: BufferedEvent[] = [];

  #consent: Consent;
  #lastEventKey: { key: string; at: number } | null = null;
  #lastPage: { url: string; at: number } | null = null;
  #warnedNoKey = false;

  constructor(options: ClientBaseOptions = {}) {
    this.#opts = {
      posthogHost: options.posthogHost ?? 'https://eu.posthog.com',
      debug: options.debug ?? readProcessEnv('ANALYTICS_DEBUG') === '1',
      anonymizeIp: options.anonymizeIp ?? true,
      salt: options.salt ?? readProcessEnv('ANALYTICS_SALT') ?? 'dev-salt',
      ...options,
    };
    this.#sink =
      options.sink ??
      ((message, payload) => {
        console.warn(message, payload);
      });
    this.#consent = options.consent ?? 'pending';
  }

  /**
   * Internal buffer — primarily for tests. Production code should not rely on
   * this (use `testing.createMockAnalytics` instead).
   */
  get events(): readonly BufferedEvent[] {
    return this.#events;
  }

  track<E extends EventName>(event: E, properties?: EventMap[E], scope?: Partial<Scope>): void {
    if (this.#consent === 'denied') return;

    const key = `${event}:${stableStringify(properties)}`;
    const now = Date.now();
    if (
      this.#lastEventKey &&
      this.#lastEventKey.key === key &&
      now - this.#lastEventKey.at < DEDUPE_WINDOW_MS
    ) {
      return;
    }
    this.#lastEventKey = { key, at: now };

    const merged = this.#enrich(properties, scope);
    this.#events.push({ kind: 'track', event, properties: merged, scope, at: now });
    this.#maybeWarnNoKey();
    if (this.#opts.debug) {
      this.#sink('[analytics] track', { event, properties: merged });
    }
  }

  identify(userId: string, traits?: IdentifyTraits): void {
    if (this.#consent === 'denied') return;
    const now = Date.now();
    this.#events.push({ kind: 'identify', properties: { userId, traits }, at: now });
    this.#maybeWarnNoKey();
    if (this.#opts.debug) {
      this.#sink('[analytics] identify', { userId, traits });
    }
  }

  page(url: string, referrer?: string): void {
    if (this.#consent === 'denied') return;
    const now = Date.now();
    if (
      this.#lastPage &&
      this.#lastPage.url === url &&
      now - this.#lastPage.at < PAGE_DEBOUNCE_MS
    ) {
      return;
    }
    this.#lastPage = { url, at: now };
    this.#events.push({ kind: 'page', properties: { url, referrer }, at: now });
    if (this.#opts.debug) {
      this.#sink('[analytics] page', { url, referrer });
    }
  }

  group(workspaceId: string, traits?: GroupTraits): void {
    if (this.#consent === 'denied') return;
    const hashed = hashWorkspaceId(workspaceId, this.#opts.salt);
    const now = Date.now();
    this.#events.push({
      kind: 'group',
      properties: { workspaceId: hashed, traits },
      at: now,
    });
    if (this.#opts.debug) {
      this.#sink('[analytics] group', { workspaceId: hashed, traits });
    }
  }

  reset(): void {
    this.#events.length = 0;
    this.#lastEventKey = null;
    this.#lastPage = null;
  }

  setConsent(consent: Consent): void {
    this.#consent = consent;
  }

  flush(): Promise<void> {
    // TODO [PHASE-2]: await real transport flush. Phase 1 is a no-op.
    return Promise.resolve();
  }

  #enrich(properties: unknown, scope?: Partial<Scope>): Record<string, unknown> {
    const base: Record<string, unknown> =
      properties && typeof properties === 'object' ? { ...(properties as object) } : {};

    const resolvedScope = scope ?? this.#opts.getScope?.() ?? null;
    if (resolvedScope?.workspaceId) {
      base['workspaceId'] = hashWorkspaceId(resolvedScope.workspaceId, this.#opts.salt);
    }
    if (resolvedScope?.userId) {
      base['userId'] = resolvedScope.userId;
    }

    if (this.#opts.anonymizeIp) {
      base['$ip'] = null;
    }

    // NOTE: autocapture is intentionally disabled at the PostHog config level
    // (not here) so we never ship pageviews/clicks we didn't model. Same for
    // session recording (session_recording.enable: false) — privacy by default.
    return base;
  }

  #maybeWarnNoKey(): void {
    if (this.#warnedNoKey) return;
    if (!this.#opts.posthogKey) return;
    // Key is present but Phase 1 doesn't ship a transport yet.
    this.#warnedNoKey = true;
    this.#sink('[analytics] posthog-js not loaded yet (Phase 2) — buffering events only', {});
  }
}

function stableStringify(value: unknown): string {
  if (value === undefined) return '';
  // Deterministic enough for dedupe bucket — real tests never depend on
  // ordering of two distinct property objects.
  return JSON.stringify(value);
}
